/**
 * useRoutineActions — the mutations for one routine editor instance.
 *
 * Split out of the old `useWorkflow` (P12 §6.2). Reads the draft from a {@link RoutineDraftStore}
 * and owns the side-effectful actions:
 *   - `save`      — persist the draft (auto-saved quietly for drafts, explicit for active; §L11).
 *                   On success flips the store chip to `saved` via `store.markSaved()`.
 *   - `dryRun`    — preview the current on-screen draft (real query, no send), via `useDryRun`.
 *   - `start`     — the renamed **Activate** CTA (§L11: Start saves+activates a live routine).
 *                   Under the hood still calls the existing oRPC `workflow.activate` procedure.
 *   - `pause`     — pause a live routine (oRPC `workflow.pause`).
 *   - `runNow`    — manual one-off run (oRPC `workflow.runNow`).
 *   - `remove`    — delete the routine (oRPC `workflow.delete`).
 *
 * Only the UI label/seam changed (activate → start); the oRPC procedures are unchanged.
 * Start/Pause/Run-now/Delete remain owner/admin-gated server-side; this hook is the call site.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { ROUTINE_REPAIRED_SINCE_LOAD } from '@/_shared/components/AccountRepair/copy';
import {
  isAccountNeedsRepairError,
  isRepairConflictError,
  refusalVerdict,
} from '@/_shared/components/AccountRepair/repairErrors';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';

import { focusFirstInvalidField, hasValidationErrors, validateRoutineDraft } from '../lib/validation';
import { draftToGraph, scheduleToTrigger } from '../mappers';
import type { StepKey } from '../types';
import { useDryRun, type UseDryRunReturn } from './useDryRun';
import type { RoutineDraftStore } from './useRoutineDraft';

export interface UseRoutineActionsReturn {
  save: () => void;
  isSaving: boolean;
  /** Persist a name/description rename (saved routines only); resolves on success, rejects on failure. */
  rename: (name: string, description: string) => Promise<void>;
  isRenaming: boolean;
  /** Trigger a dry-run of the current on-screen draft (no save required). */
  runDryRun: () => void;
  dryRun: UseDryRunReturn;
  /** Discard the durable working copy → revert to the published graph (Issue 11b). */
  discardDraft: () => void;
  isDiscarding: boolean;
  /** Start = save+activate the routine (renamed from Activate; calls oRPC workflow.activate). */
  start: () => void;
  isStarting: boolean;
  pause: () => void;
  isPausing: boolean;
  runNow: () => void;
  isRunningNow: boolean;
  remove: () => void;
  isRemoving: boolean;
}

export function useRoutineActions(store: RoutineDraftStore, draftSessionKey?: string): UseRoutineActionsReturn {
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  const { wp } = useWorkspace();
  const navigate = useNavigate();

  const {
    workflowId,
    draft,
    setStaleSteps,
    markSaved,
    saveState,
    markDraftPersisted,
    discardWorkingCopy,
    markSubmitAttempted,
    applyRenameLocal,
  } = store;

  // Debounce window for the durable working-copy autosave (Issue 11b). Long enough to coalesce a
  // burst of keystrokes / agent patches into one write, short enough that a draft is durable within a
  // second of the user pausing.
  const AUTOSAVE_DEBOUNCE_MS = 1000;

  const listQueryKey = orpc.workflow.list.queryOptions({ input: {} }).queryKey;
  const getQueryKey = (id: string): readonly unknown[] => orpc.workflow.get.queryOptions({ input: { id } }).queryKey;

  // Autosave supersession guard. A Save (promote+delete working copy) or Discard (delete working copy)
  // invalidates any pending or in-flight autosave, so a late `saveDraft` can't resurrect the working
  // copy the action just removed: the timer ref cancels a not-yet-sent send, and the epoch ref makes
  // an already-sent `saveDraft`'s success a no-op (it won't flip `hasWorkingCopy` back on).
  const autosaveTimerRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const autosaveEpochRef = React.useRef(0);
  // Bumped when a Save/Discard FAILS — the action cancelled the pending autosave up-front, but nothing
  // was actually deleted server-side, so the autosave effect must re-arm (its other deps don't change
  // on failure since saveState stays 'unsaved'). Without this, edits made just before a failed Save
  // are never durably written (Issue 11b reload-survival regression).
  const [autosaveRearmNonce, setAutosaveRearmNonce] = React.useState(0);
  const cancelPendingAutosave = React.useCallback((): void => {
    autosaveEpochRef.current += 1;
    if (autosaveTimerRef.current !== undefined) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = undefined;
    }
  }, []);

  // ── Dry run ───────────────────────────────────────────────────────────────
  const dryRun = useDryRun({
    onStaleCleared: (cleared) => {
      setStaleSteps((prev) => {
        const next = new Set<StepKey>(prev);
        cleared.forEach((s) => next.delete(s));
        return next;
      });
    },
  });
  // Form guard for Save / Preview: reject a draft with empty required fields (warehouse, SQL, channel,
  // message, or an emptied threshold) instead of persisting/previewing it. On a block it reveals every
  // field's error inline (markSubmitAttempted) and scrolls+focuses the first invalid field, then
  // returns false — no toast, since the inline error states already communicate what's wrong.
  const passesSubmitValidation = React.useCallback((): boolean => {
    const errors = validateRoutineDraft(draft);
    if (!hasValidationErrors(errors)) return true;
    markSubmitAttempted();
    focusFirstInvalidField(errors);
    return false;
  }, [draft, markSubmitAttempted]);

  const runDryRun = React.useCallback(() => {
    if (!passesSubmitValidation()) return;
    dryRun.run(draft);
  }, [dryRun, draft, passesSubmitValidation]);

  // ACT2-1286: a save refused because a step was repaired after this editor loaded. Refetch the routine
  // so the editor follows the repair, and tell the user to check it and save again.
  const followRepairedSinceLoad = React.useCallback((): void => {
    if (workflowId) {
      void queryClient.invalidateQueries({
        queryKey: orpc.workflow.get.queryOptions({ input: { id: workflowId } }).queryKey,
      });
    }
    toast.error(ROUTINE_REPAIRED_SINCE_LOAD);
  }, [workflowId, queryClient, orpc]);

  // ── Save ──────────────────────────────────────────────────────────────────
  const saveMutation = useMutation(
    orpc.workflow.save.mutationOptions({
      onSuccess: (data) => {
        const id = data.workflow.id;
        markSaved();
        void queryClient.invalidateQueries({ queryKey: listQueryKey });
        if (!workflowId) {
          // First save: navigate to /workflows/:id and replace history.
          void navigate(wp(`/workflows/${id}`), { replace: true });
        } else {
          void queryClient.invalidateQueries({ queryKey: getQueryKey(id) });
        }
        toast.success('Routine saved');
      },
      onError: (err) => {
        if (isRepairConflictError(err)) {
          // ACT2-1286: a step was repaired after this editor opened. Reload the routine; the editor then
          // follows the repaired account (useRoutineDraft), and the user saves again. Not re-armed: the
          // autosave would send the pre-repair account back and be refused the same way.
          followRepairedSinceLoad();
          return;
        }
        // The Save cancelled the pending autosave but deleted nothing — re-arm it so the edit is still
        // durably written despite the failed Save.
        setAutosaveRearmNonce((n) => n + 1);
        toast.error(err instanceof Error ? err.message : 'Failed to save routine');
      },
    }),
  );

  const save = React.useCallback((): void => {
    if (!passesSubmitValidation()) return;
    // Supersede any pending/in-flight autosave — this Save promotes+deletes the working copy, so a late
    // autosave must not resurrect it.
    cancelPendingAutosave();
    saveMutation.mutate({
      id: workflowId,
      name: draft.name || 'New routine',
      description: draft.description,
      kind: draft.condition ? 'alert' : 'notification',
      trigger: scheduleToTrigger(draft.schedule),
      graph: draftToGraph(draft),
      // On a FIRST save (no workflowId), hand the backend the draftLocalId the RoutineBuilder chat
      // session was authored under so it re-keys that session to the new id — otherwise the authoring
      // history is orphaned and reopening /workflows/:id shows an empty chat (Issue 9 / §4.5).
      draftSessionKey: workflowId ? undefined : draftSessionKey,
    });
  }, [saveMutation, workflowId, draft, draftSessionKey, cancelPendingAutosave, passesSubmitValidation]);

  // ── Rename (name/description only — ACT2-18) ────────────────────────────────
  // Only reachable for a SAVED routine (the Rename menu item is hidden until the routine is saved). The
  // dialog awaits this promise and closes only once the API call succeeds; on failure it stays open and
  // the mutation's onError toasts. On success we update the on-screen title via `applyRenameLocal` — a
  // same-id `workflow.get` refetch won't re-seed the draft (loadedId guard), so the update must be explicit.
  const renameMutation = useMutation(
    orpc.workflow.rename.mutationOptions({
      onSuccess: (data) => {
        applyRenameLocal(data.workflow.name, data.workflow.description ?? '');
        void queryClient.invalidateQueries({ queryKey: listQueryKey });
        toast.success('Routine renamed');
        // Returned (not `void`) so the mutation stays pending until the `get` refetch lands — the rename
        // dialog stays locked until the freshly persisted name is in the cache. Otherwise a Discard fired
        // in the refetch window would re-seed the draft from the stale published row (finding #3).
        return queryClient.invalidateQueries({ queryKey: getQueryKey(data.workflow.id) });
      },
      onError: (err) => {
        // Re-arm the autosave the rename's cancel dropped — a failed rename deleted nothing, so an edit
        // made just before it must still be durably written (mirrors the Save/Discard re-arm).
        setAutosaveRearmNonce((n) => n + 1);
        toast.error(err instanceof Error ? err.message : 'Failed to rename routine');
      },
    }),
  );

  const rename = React.useCallback(
    async (name: string, description: string): Promise<void> => {
      if (!workflowId) return; // defensive — Rename is hidden until the routine is saved
      // Supersede any pending/in-flight autosave — a late saveDraft carrying the pre-rename name must not
      // clobber the working copy the rename just updated (same guard Save/Discard use).
      cancelPendingAutosave();
      await renameMutation.mutateAsync({ id: workflowId, name, description });
    },
    [renameMutation, workflowId, cancelPendingAutosave],
  );

  // ── Autosave (durable working copy — Issue 11b) ─────────────────────────────
  // Debounced upsert of the current on-screen draft into the user's server-side working copy, so an
  // unsaved edit survives a post-turn refetch, a reload, and a browser close. Only for ALREADY-SAVED
  // routines (a brand-new routine has no published id to anchor a working copy — it stays client-only
  // until the first Save). Fires only on genuine new edits (`saveState === 'unsaved'`), never on a
  // freshly-loaded working copy. `mutate` from react-query is referentially stable.
  const { mutate: saveDraftMutate } = useMutation(
    // Success is handled per-call (below) so it can honor the supersession epoch; a transient failure
    // is best-effort + retried by the next edit and must not interrupt the user with a toast.
    orpc.workflow.saveDraft.mutationOptions({}),
  );
  React.useEffect(() => {
    if (!workflowId) return;
    if (saveState !== 'unsaved') return;
    // Autosave ALWAYS runs for a saved routine (an emptied threshold must not block persisting the rest
    // of the working copy). An empty threshold is NOT coerced to `rhs: 0` — `conditionToConfig` omits it
    // so it round-trips back to an empty field on reload instead of resurrecting a `row_count <op> 0` the
    // user cleared. The explicit Save/Preview/run boundaries still reject an empty threshold.
    // Snapshot the epoch at schedule time; a Save/Discard between now and this autosave's success bumps
    // it, so the per-call onSuccess below skips markDraftPersisted for a superseded working copy.
    const scheduledEpoch = autosaveEpochRef.current;
    const handle = setTimeout(() => {
      autosaveTimerRef.current = undefined;
      saveDraftMutate(
        {
          id: workflowId,
          name: draft.name || 'New routine',
          description: draft.description,
          kind: draft.condition ? 'alert' : 'notification',
          trigger: scheduleToTrigger(draft.schedule),
          graph: draftToGraph(draft),
        },
        {
          onSuccess: () => {
            if (scheduledEpoch === autosaveEpochRef.current) markDraftPersisted();
          },
          // Other autosave failures stay silent (retried by the next edit); this one needs a reload.
          onError: (err) => {
            if (isRepairConflictError(err)) followRepairedSinceLoad();
          },
        },
      );
    }, AUTOSAVE_DEBOUNCE_MS);
    autosaveTimerRef.current = handle;
    return () => clearTimeout(handle);
    // autosaveRearmNonce re-arms the timer after a failed Save/Discard (deps otherwise unchanged then).
  }, [workflowId, saveState, draft, saveDraftMutate, markDraftPersisted, autosaveRearmNonce, followRepairedSinceLoad]);

  // ── Discard draft (revert to published — Issue 11b) ─────────────────────────
  const discardMutation = useMutation(
    orpc.workflow.discardDraft.mutationOptions({
      onSuccess: () => {
        discardWorkingCopy();
        if (workflowId) void queryClient.invalidateQueries({ queryKey: getQueryKey(workflowId) });
        toast.success('Draft discarded');
      },
      onError: (err) => {
        // The Discard cancelled the pending autosave but deleted nothing — re-arm it.
        setAutosaveRearmNonce((n) => n + 1);
        toast.error(err instanceof Error ? err.message : 'Failed to discard draft');
      },
    }),
  );

  const discardDraft = React.useCallback((): void => {
    if (!workflowId) return;
    // Supersede any pending/in-flight autosave — this Discard deletes the working copy, so a late
    // autosave must not resurrect it with the just-discarded edits.
    cancelPendingAutosave();
    discardMutation.mutate({ id: workflowId });
  }, [discardMutation, workflowId, cancelPendingAutosave]);

  // ── Start (= activate) ──────────────────────────────────────────────────────
  const startMutation = useMutation(
    orpc.workflow.activate.mutationOptions({
      onSuccess: (data) => {
        const id = data.workflow.id;
        void queryClient.invalidateQueries({ queryKey: getQueryKey(id) });
        void queryClient.invalidateQueries({ queryKey: listQueryKey });
        toast.success('Routine started — it will run on schedule');
      },
      onError: (err, variables) => {
        // ACT2-1286: Resume refused — a step still needs repair, or its probe failed. Refresh so the
        // banner shows which; the toast carries the server's reason (the probe's own error).
        if (isAccountNeedsRepairError(err)) {
          void queryClient.invalidateQueries({ queryKey: getQueryKey(variables.id) });
          void queryClient.invalidateQueries({ queryKey: listQueryKey });
          toast.error(refusalVerdict(err) ? `Couldn't resume: ${err.message}` : err.message);
          return;
        }
        toast.error(err instanceof Error ? err.message : 'Failed to start routine');
      },
    }),
  );

  const start = React.useCallback((): void => {
    if (!workflowId) {
      toast.error('Save the routine before starting');
      return;
    }
    startMutation.mutate({ id: workflowId });
  }, [startMutation, workflowId]);

  // ── Pause ───────────────────────────────────────────────────────────────────
  const pauseMutation = useMutation(
    orpc.workflow.pause.mutationOptions({
      onSuccess: (data) => {
        const id = data.workflow.id;
        void queryClient.invalidateQueries({ queryKey: getQueryKey(id) });
        void queryClient.invalidateQueries({ queryKey: listQueryKey });
        toast.success('Routine paused');
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : 'Failed to pause routine');
      },
    }),
  );

  const pause = React.useCallback((): void => {
    if (!workflowId) return;
    pauseMutation.mutate({ id: workflowId });
  }, [pauseMutation, workflowId]);

  // ── Run now ───────────────────────────────────────────────────────────────────
  const runNowMutation = useMutation(
    orpc.workflow.runNow.mutationOptions({
      onSuccess: () => {
        toast.success('Run enqueued — it will post to Slack when it finishes');
      },
      onError: (err, variables) => {
        // ACT2-1286: a Run now that found a removed account paused the routine; refresh to show it.
        // `CONFLICT` stays "already running".
        if (isAccountNeedsRepairError(err)) {
          void queryClient.invalidateQueries({ queryKey: getQueryKey(variables.id) });
          void queryClient.invalidateQueries({ queryKey: listQueryKey });
        }
        toast.error(err instanceof Error ? err.message : 'Failed to run routine');
      },
    }),
  );

  const runNow = React.useCallback((): void => {
    if (!workflowId) {
      toast.error('Save and start the routine before running');
      return;
    }
    runNowMutation.mutate({ id: workflowId });
  }, [runNowMutation, workflowId]);

  // ── Delete ──────────────────────────────────────────────────────────────────
  const removeMutation = useMutation(
    orpc.workflow.delete.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: listQueryKey });
        toast.success('Routine deleted');
        void navigate(wp('/workflows'));
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : 'Failed to delete routine');
      },
    }),
  );

  const remove = React.useCallback((): void => {
    if (!workflowId) return;
    removeMutation.mutate({ id: workflowId });
  }, [removeMutation, workflowId]);

  return {
    save,
    isSaving: saveMutation.isPending,
    rename,
    isRenaming: renameMutation.isPending,
    runDryRun,
    dryRun,
    discardDraft,
    isDiscarding: discardMutation.isPending,
    start,
    isStarting: startMutation.isPending,
    pause,
    isPausing: pauseMutation.isPending,
    runNow,
    isRunningNow: runNowMutation.isPending,
    remove,
    isRemoving: removeMutation.isPending,
  };
}
